import { Suspense, useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { PanelLeftClose, PanelLeft } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import TenantSwitcher from './TenantSwitcher'
import UserMenu from './UserMenu'
import SidebarNav from './SidebarNav'
import MobileBottomNav from './MobileBottomNav'
import NotificationBadge from '@/components/header/NotificationBadge'
import AiChatButton from '@/components/header/AiChatButton'
import AssistantPanelMount from '@/components/ai-chat/AssistantPanelMount'
import AssistSpotlight from '@/components/assist/AssistSpotlight'
import CommandPalette from '@/components/CommandPalette'
import KeyboardShortcutsDialog from '@/components/KeyboardShortcutsDialog'
import { TrialBanner } from '@/components/subscription/TrialBanner'
import RockSetupBanner from './RockSetupBanner'
import ImpersonationBanner from './ImpersonationBanner'
import ImpersonationAdminBlockedNotice from './ImpersonationAdminBlockedNotice'
import UpgradePromptDialog from '@/components/subscription/UpgradePromptDialog'
import { useLimitExceededDialog } from '@/hooks/useLimitExceededDialog'
import { useDynamicTitle } from '@/hooks/useDynamicTitle'
import CardSkeleton from '@/components/common/CardSkeleton'
import { PageContainer } from '@/components/ui/page-container'
import { Skeleton } from '@/components/ui/skeleton'
import AppErrorBoundary from '@/components/common/AppErrorBoundary'
import {
  fetchCurrentSubscription,
  selectIsTrialExpired
} from '@/features/subscriptions/subscriptionsSlice'
import { fetchFavorites } from '@/features/favorites/favoritesSlice'
import BrandingDocument from '@/components/branding/BrandingDocument'
import BrandingAttribution from '@/components/branding/BrandingAttribution'
import { PageContextProvider } from '@/features/aiChat/pageContext'
import { useAssistantShortcut } from '@/features/aiChat/useAssistantShortcut'
import { usePendingRockSetupRedirect } from '@/pages/onboarding/rock-setup/usePendingRockSetupRedirect'
import { MAIN_CONTAINER_CLASS, SIDEBAR_COLLAPSED_WIDTH_PX, SIDEBAR_WIDTH_PX } from './shellSizes'
import { useSkin } from '@/theme/skin'
import { GraphitPaneHeader } from './GraphitPaneHeader'

/**
 * Root layout for authenticated pages with sidebar navigation and header
 * @returns {JSX.Element}
 */
export default function RootLayout() {
  const skin = useSkin()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isAuthenticated } = useSelector((state) => state.auth)
  const { isOpen: limitDialogOpen, error: limitError, close: closeLimitDialog } = useLimitExceededDialog()

  // Trial status
  const isTrialExpired = useSelector(selectIsTrialExpired)

  // Fetch subscription and favorites on mount
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCurrentSubscription())
      dispatch(fetchFavorites())
    }
  }, [dispatch, isAuthenticated])

  // Redirect to paywall if trial expired
  useEffect(() => {
    if (isTrialExpired && !location.pathname.startsWith('/trial-expired') && !location.pathname.startsWith('/settings/subscription')) {
      navigate('/trial-expired', { replace: true })
    }
  }, [isTrialExpired, location.pathname, navigate])

  // Guided Rock setup unfinished: send an owner/admin into it once per
  // session (ROCK-ONBOARDING-REWORK T4). The paywall wins when both apply.
  usePendingRockSetupRedirect({ isBlocked: isTrialExpired })

  useAssistantShortcut()

  // Get dynamic title based on current route
  const pageTitle = useDynamicTitle()

  // Sidebar collapsed state (persisted to localStorage)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('graphit-sidebar-collapsed')
    return stored === 'true'
  })

  useEffect(() => {
    localStorage.setItem('graphit-sidebar-collapsed', String(sidebarCollapsed))
  }, [sidebarCollapsed])

  // The rail changes only through its explicit control, never on pointer hover.
  const showIconsOnly = sidebarCollapsed

  return (
    <TooltipProvider delayDuration={300}>
      {/* Page-awareness for the AI assistant (B4): wraps Outlet (page
          headers call setPageContext) and AiChatPanel (reads pageContext)
          so both share one instance. Provider-and-shortcut-only change to
          this file — everything else about the shell is untouched. */}
      <PageContextProvider>
      <BrandingDocument title={pageTitle} />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-surface focus:text-brand focus:rounded-control focus:shadow-overlay focus:ring-2 focus:ring-brand"
      >
        Skip to content
      </a>
      {/* bg-canvas, not bg-surface-2: canvas is the page-shell tint every
          routed page's cards sit on (visual direction analysis §2 polarity
          inversion); surface-2 is a distinct, slightly darker token reserved
          for secondary chrome (tab-list track, skeleton shimmer, disabled
          fills) — using it here tinted every page in the app one step too
          dark and off the card/canvas contrast the spec measures. */}
      <div data-shell="frame" className="relative flex h-dvh overflow-hidden bg-canvas">
        {/* Sidebar spacer - maintains layout flow width. Width tokens
            (--size-sidebar / --size-sidebar-collapsed, index.css) so the
            spacer and the real <aside> below can never drift apart. */}
        <div
          className={`hidden md:block shrink-0 transition-all duration-300 ${
            sidebarCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar'
          }`}
        />

        {/* Sidebar width follows the explicit, persisted collapse preference. */}
        <aside
          data-shell="rail"
          className={`hidden md:flex flex-col absolute inset-y-0 left-0 bg-navigation text-navigation-ink border-r border-navigation-line z-30 overflow-hidden transition-[width,box-shadow] duration-300 ease-in-out ${
            showIconsOnly ? 'w-sidebar-collapsed' : 'w-sidebar'
          }`}
        >
          {/* Same height token as the main topbar below, so the sidebar's
              brand row and the content header line up as one toolbar row
              (shell standardization, F-7 addendum). */}
          <div data-shell="rail-header" className="border-b border-navigation-line flex h-topbar shrink-0 items-center px-3">
            {skin === 'fluent3d' ? <GraphitPaneHeader collapsed={showIconsOnly} /> : <TenantSwitcher collapsed={showIconsOnly} />}
          </div>

          {/* Superuser impersonation (86e39gctg): pinned under the brand
              row, never dismissable; a badge while the rail is collapsed. */}
          <ImpersonationBanner variant={showIconsOnly ? 'rail' : 'sidebar'} />

          <nav id="desktop-navigation" className="mt-4 flex min-h-0 flex-1 flex-col px-2" aria-label="Main navigation">
            <SidebarNav showIconsOnly={showIconsOnly} />
          </nav>

          {/* A single visible toggle retains keyboard focus as the rail changes width. */}
          <div data-shell="rail-footer" className="shrink-0 border-t border-navigation-line px-2 pt-2 pb-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSidebarCollapsed(prev => !prev)
                  }}
                  className={`h-10 w-full text-navigation-muted hover:bg-navigation-active/60 hover:text-navigation-active-ink focus-visible:ring-navigation-ink focus-visible:ring-offset-navigation ${showIconsOnly ? 'justify-center px-2.5' : 'justify-start px-3'}`}
                  aria-label={sidebarCollapsed ? 'Pin sidebar open' : 'Collapse sidebar'}
                  aria-expanded={!sidebarCollapsed}
                  aria-controls="desktop-navigation"
                >
                  {sidebarCollapsed ? <PanelLeft className="size-5" aria-hidden="true" /> : <PanelLeftClose className="size-5" aria-hidden="true" />}
                  <span className={showIconsOnly ? 'sr-only' : 'ml-1 truncate'}>{sidebarCollapsed ? 'Pin sidebar open' : 'Collapse sidebar'}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{sidebarCollapsed ? 'Pin sidebar open' : 'Collapse sidebar'}</TooltipContent>
            </Tooltip>
            {!showIconsOnly && skin !== 'fluent3d' && <BrandingAttribution className="px-3 pt-2 pb-1 text-navigation-muted" />}
          </div>
        </aside>

        {/* Main content */}
        <main id="main-content" tabIndex={-1} className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Phone width, where the sidebar above is hidden */}
          <ImpersonationBanner variant="bar" />
          <ImpersonationAdminBlockedNotice />
          {/* Trial Banner - shown when on trial */}
          <TrialBanner />
          {/* Rock linked but its guided setup unfinished (admins only) */}
          {isAuthenticated && <RockSetupBanner />}

          {/* Menu bar (F-7 addendum "standardize menu bar"): one height
              token shared with the sidebar's brand row above, a translucent
              blurred surface (the same bg/blur recipe FormSaveBar already
              uses for a pinned bar), and a hairline bottom edge on the one
              line token. */}
          <header data-shell="topbar" className="flex h-[calc(var(--size-topbar)+env(safe-area-inset-top))] shrink-0 items-center justify-between gap-2 border-b border-line bg-surface supports-[backdrop-filter]:bg-surface/80 backdrop-blur-md px-4 pt-[env(safe-area-inset-top)] md:h-topbar md:px-8 md:pt-0">
            <div data-shell="topbar-search" className="flex items-center gap-2 flex-1 min-w-0 max-w-md">
              <div className="flex-1 min-w-0">
                <CommandPalette />
                <KeyboardShortcutsDialog />
              </div>
            </div>
            <div data-shell="topbar-actions" className="flex shrink-0 items-center gap-1 md:gap-3">
              {skin === 'fluent3d' && isAuthenticated && <div className="max-md:hidden"><TenantSwitcher variant="chip" /></div>}
              {isAuthenticated && <AiChatButton />}
              {isAuthenticated && <NotificationBadge />}
              <UserMenu />
            </div>
          </header>
          {/* The page's own width, as a container (MAIN_CONTAINER_CLASS): what
              its layout responds to, since the assistant panel can take 420px
              of the window. */}
          <div data-shell="content" className={`${MAIN_CONTAINER_CLASS} min-h-0 flex-1 overflow-y-auto overscroll-contain md:pb-[env(safe-area-inset-bottom)]`}>
            <Suspense fallback={
              <PageContainer className="space-y-6">
                <div className="space-y-3" aria-hidden="true">
                  <Skeleton className="h-9 w-1/3" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <CardSkeleton count={6} />
              </PageContainer>
            }>
              {/* Keyed on the pathname so React remounts the boundary on
                  navigation: a page that throws recovers on its own once the
                  user navigates away, instead of wedging the whole app. The
                  header and sidebar stay usable either way. */}
              <AppErrorBoundary name="page" fullScreen={false} key={location.pathname}>
                <Outlet />
              </AppErrorBoundary>
            </Suspense>
          </div>
          <MobileBottomNav />
        </main>

        {/* AI assistant side panel (renders nothing while closed). The
            shortcut, button and open state live in the shell. It docks only
            where the page keeps enough width beside the sidebar. */}
        <AssistantPanelMount reservedWidth={sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH_PX : SIDEBAR_WIDTH_PX} />
      </div>

      {/* Rings the control an assistant "show me" card points at, after the
          viewer clicks the card (renders nothing otherwise). */}
      <AssistSpotlight />

      {/* Global upgrade prompt for plan limit exceeded errors */}
      <UpgradePromptDialog
        isOpen={limitDialogOpen}
        error={limitError}
        onClose={closeLimitDialog}
      />
      </PageContextProvider>
    </TooltipProvider>
  )
}
